/**
 * Settings, presets and pure helpers for the Render Studio (cloud admins
 * only). Nothing here touches WebGL, so it is unit-tested on its
 * own; the renderer lives in render-studio.ts.
 */

export type RenderResolutionId = 'uhd' | 'uhd-portrait' | 'square' | 'viewport';
export type RenderLensId = 'ortho' | '35' | '50' | '85' | '135';
export type RenderFramingId = 'fit' | 'viewport';
export type StudioLightingId = 'softbox' | 'showroom' | 'catalogue' | 'dusk';
export type StudioMaterialId =
  | 'as-shown'
  | 'aluminium'
  | 'blasted-aluminium'
  | 'brushed-aluminium'
  | 'titanium'
  | 'steel'
  | 'brushed-steel'
  | 'chrome'
  | 'cast-iron'
  | 'brass'
  | 'copper'
  | 'anodized-black'
  | 'anodized-blue'
  | 'anodized-red'
  | 'anodized-gold'
  | 'powder-white'
  | 'plastic-black'
  | 'rubber'
  | 'nylon'
  | 'resin-grey'
  | 'carbon-fibre'
  | 'clay';
/** A part's own choice; 'model' follows the model-wide material. */
export type PartMaterialChoice = StudioMaterialId | 'model';
export type StudioBackgroundId = 'gradient' | 'cove' | 'solid' | 'transparent';
export type RenderToneMappingId = 'agx' | 'aces' | 'neutral';
export type ColorGradeId = 'none' | 'warm' | 'cool' | 'crisp' | 'matte' | 'mono';
export type RenderQualityId = 'draft' | 'high' | 'ultra';
export type RenderOutputId = 'still' | 'turntable';
/** What a video shows: the model turning, or the input model dissolving into the optimized one as it turns. */
export type RenderAnimationId = 'turntable' | 'morph';
export type VideoSizeId = 'hd' | 'uhd';

export interface RenderStudioSettings {
  /** A PNG still, or an MP4 of the camera turning a full circle around the model. */
  output: RenderOutputId;
  /** What the video shows. */
  animation: RenderAnimationId;
  /** Whether the camera turns a full circle during a morph; a turntable always turns. */
  morphTurntable: boolean;
  /** Shape of the frame; a turntable scales it to `videoSize`. */
  resolution: RenderResolutionId;
  videoSize: VideoSizeId;
  /** Length of the video, seconds; a turning camera makes one full turn in it. */
  turntableSeconds: number;
  lens: RenderLensId;
  framing: RenderFramingId;
  /** Camera turn around the model's vertical axis from the editor view, degrees. */
  orbit: number;
  /** Camera raised (+) or lowered (−) from the editor view, degrees. */
  tilt: number;
  lighting: StudioLightingId;
  material: StudioMaterialId;
  /** Per-part overrides of `material`, by part key (see RenderStudioHost.partOf). Parts not listed follow `material`. */
  partMaterials: Readonly<Record<string, StudioMaterialId>>;
  background: StudioBackgroundId;
  /** Used when `background` is 'solid'. CSS hex, eg '#ffffff'. */
  backgroundColor: string;
  /** Clear coat on the cove: 0 = matt, 1 = a glossy floor that reflects the model. */
  floorGloss: number;
  groundShadow: boolean;
  /** 0 = hard shadows, 1 = a very large soft light. */
  shadowSoftness: number;
  ambientOcclusion: boolean;
  /** 0..1 */
  aoIntensity: number;
  bloom: boolean;
  /** 0 = everything sharp, 1 = shallow focus. Perspective lenses only. */
  depthOfField: number;
  /** 0..1 */
  vignette: number;
  edges: boolean;
  /** Rounds sharp edges so they catch highlights: 0 = sharp, 1 = the largest round. */
  bevel: number;
  toneMapping: RenderToneMappingId;
  /** Exposure in stops, -2..2. */
  exposure: number;
  /** Colour look over the tone-mapped image; a solid backdrop keeps its exact colour. */
  grade: ColorGradeId;
  /** Film grain, 0..1. */
  grain: number;
  /** Colour fringing towards the corners, as a real lens shows, 0..1. */
  lensFringe: number;
  quality: RenderQualityId;
  /** InfinitForm logo in the bottom-right corner. */
  watermark: boolean;
}

export const DEFAULT_RENDER_STUDIO_SETTINGS: Readonly<RenderStudioSettings> = {
  output: 'still',
  animation: 'turntable',
  morphTurntable: true,
  resolution: 'uhd',
  videoSize: 'hd',
  turntableSeconds: 6,
  lens: '85',
  framing: 'fit',
  orbit: 0,
  tilt: 0,
  lighting: 'softbox',
  material: 'as-shown',
  partMaterials: {},
  background: 'gradient',
  backgroundColor: '#ffffff',
  floorGloss: 0.4,
  groundShadow: true,
  shadowSoftness: 0.5,
  ambientOcclusion: true,
  aoIntensity: 0.8,
  bloom: true,
  depthOfField: 0,
  vignette: 0.25,
  edges: false,
  bevel: 0.5,
  toneMapping: 'aces',
  exposure: 0,
  grade: 'none',
  grain: 0,
  lensFringe: 0,
  quality: 'high',
  watermark: true
};

/** Long edge of every 4K output. */
export const RENDER_LONG_EDGE = 3840;
/** Long edge of the live preview in the dialog. */
export const PREVIEW_LONG_EDGE = 1280;
/** Samples the live preview accumulates before it stops. */
export const PREVIEW_SAMPLES = 24;
/** Range of the Tilt control, degrees. */
export const TILT_LIMIT = 80;
/** The camera never goes past this elevation, so it can't flip over the top. */
export const MAX_ELEVATION = 85;

export interface LabelledOption<T extends string> {
  value: T;
  label: string;
}

export const RESOLUTION_OPTIONS: readonly LabelledOption<RenderResolutionId>[] = [
  { value: 'uhd', label: '4K UHD · 3840 × 2160' },
  { value: 'uhd-portrait', label: '4K portrait · 2160 × 3840' },
  { value: 'square', label: '4K square · 3840 × 3840' },
  { value: 'viewport', label: '4K, viewport shape' }
];

export const LENS_OPTIONS: readonly LabelledOption<RenderLensId>[] = [
  { value: 'ortho', label: 'Orthographic' },
  { value: '35', label: '35 mm wide' },
  { value: '50', label: '50 mm standard' },
  { value: '85', label: '85 mm portrait' },
  { value: '135', label: '135 mm telephoto' }
];

export const FRAMING_OPTIONS: readonly LabelledOption<RenderFramingId>[] = [
  { value: 'fit', label: 'Fit model' },
  { value: 'viewport', label: 'Match viewport' }
];

export const TONE_MAPPING_OPTIONS: readonly LabelledOption<RenderToneMappingId>[] = [
  { value: 'agx', label: 'AgX filmic' },
  { value: 'aces', label: 'ACES filmic' },
  { value: 'neutral', label: 'Neutral (true colour)' }
];

/** A colour look in display space: white balance, then saturation, contrast about mid grey, and a raised black. */
export interface ColorGrade {
  label: string;
  tint: [number, number, number];
  saturation: number;
  contrast: number;
  lift: number;
}

export const COLOR_GRADES: Readonly<Record<ColorGradeId, ColorGrade>> = {
  none: { label: 'None', tint: [1, 1, 1], saturation: 1, contrast: 1, lift: 0 },
  warm: { label: 'Warm', tint: [1.04, 1, 0.93], saturation: 1.05, contrast: 1.02, lift: 0 },
  cool: { label: 'Cool', tint: [0.95, 0.99, 1.05], saturation: 0.97, contrast: 1.03, lift: 0 },
  crisp: { label: 'Crisp', tint: [1, 1, 1], saturation: 1.12, contrast: 1.1, lift: 0 },
  matte: { label: 'Matte', tint: [1, 1, 1], saturation: 0.9, contrast: 0.92, lift: 0.04 },
  mono: { label: 'Black and white', tint: [1, 1, 1], saturation: 0, contrast: 1.08, lift: 0 }
};

export const GRADE_OPTIONS: readonly LabelledOption<ColorGradeId>[] = (Object.keys(COLOR_GRADES) as ColorGradeId[]).map((value) => ({
  value,
  label: COLOR_GRADES[value].label
}));

export const QUALITY_OPTIONS: readonly LabelledOption<RenderQualityId>[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'high', label: 'High' },
  { value: 'ultra', label: 'Ultra' }
];

export const OUTPUT_OPTIONS: readonly LabelledOption<RenderOutputId>[] = [
  { value: 'still', label: 'Still' },
  { value: 'turntable', label: 'Video' }
];

export const ANIMATION_OPTIONS: readonly LabelledOption<RenderAnimationId>[] = [
  { value: 'turntable', label: 'Turntable' },
  { value: 'morph', label: 'Input to optimized' }
];

export const VIDEO_SIZE_OPTIONS: readonly LabelledOption<VideoSizeId>[] = [
  { value: 'hd', label: '1080p' },
  { value: 'uhd', label: '4K' }
];

export const BACKGROUND_OPTIONS: readonly LabelledOption<StudioBackgroundId>[] = [
  { value: 'gradient', label: 'Studio' },
  { value: 'cove', label: 'Cove' },
  { value: 'solid', label: 'Solid' },
  { value: 'transparent', label: 'Transparent' }
];

/** Accumulated samples per final render: each one jitters the pixel, the soft light and the lens. */
export const QUALITY_SAMPLES: Readonly<Record<RenderQualityId, number>> = {
  draft: 32,
  high: 128,
  ultra: 512
};

/** Samples per turntable frame: a video shows each frame for a moment, so it needs far fewer than a still. */
export const VIDEO_SAMPLES: Readonly<Record<RenderQualityId, number>> = {
  draft: 8,
  high: 24,
  ultra: 64
};

/** Samples the final render accumulates for these settings, per frame for a turntable. */
export function renderSamples(
  settings: Pick<RenderStudioSettings, 'quality'> & Partial<Pick<RenderStudioSettings, 'output'>>
): number {
  return (settings.output === 'turntable' ? VIDEO_SAMPLES : QUALITY_SAMPLES)[settings.quality];
}

// ── Look presets ──────────────────────────────────────────────────────

/** Basic shows the presets and the few choices most renders need; Advanced shows every setting. */
export type RenderStudioMode = 'basic' | 'advanced';
export type RenderPresetId = 'studio' | 'catalogue' | 'showroom' | 'hero' | 'technical' | 'cutout' | 'monochrome';

/** The settings a look preset sets. Output, size, quality, materials, the camera angle and the logo stay as chosen. */
export const LOOK_KEYS = [
  'lens',
  'framing',
  'lighting',
  'background',
  'backgroundColor',
  'floorGloss',
  'groundShadow',
  'shadowSoftness',
  'ambientOcclusion',
  'aoIntensity',
  'bloom',
  'depthOfField',
  'vignette',
  'edges',
  'bevel',
  'toneMapping',
  'exposure',
  'grade',
  'grain',
  'lensFringe'
] as const satisfies readonly (keyof RenderStudioSettings)[];

export type RenderLook = Pick<RenderStudioSettings, (typeof LOOK_KEYS)[number]>;

export interface RenderPreset {
  id: RenderPresetId;
  label: string;
  description: string;
  /** Changes from the default look; every other look setting goes back to its default. */
  look: Partial<RenderLook>;
  /** Needs a transparent background, which video can't carry. */
  stillOnly?: boolean;
}

export const RENDER_PRESETS: readonly RenderPreset[] = [
  {
    id: 'studio',
    label: 'Studio',
    description: 'Soft light on a neutral backdrop. A safe start for any part.',
    look: {}
  },
  {
    id: 'catalogue',
    label: 'Catalogue',
    description: 'Bright and even on pure white, for spec sheets and web shops.',
    look: { background: 'solid', backgroundColor: '#ffffff', lens: '50', vignette: 0, grade: 'crisp', shadowSoftness: 0.7 }
  },
  {
    id: 'showroom',
    label: 'Showroom',
    description: 'Dark glossy floor that reflects the part.',
    look: { lighting: 'showroom', background: 'cove', floorGloss: 0.7, vignette: 0.35, grade: 'cool' }
  },
  {
    id: 'hero',
    label: 'Hero shot',
    description: 'Low warm light, shallow focus and film grain for a cover image.',
    look: {
      lighting: 'dusk',
      background: 'cove',
      floorGloss: 0.5,
      lens: '135',
      depthOfField: 0.35,
      shadowSoftness: 0.3,
      vignette: 0.45,
      toneMapping: 'agx',
      grade: 'warm',
      grain: 0.15,
      lensFringe: 0.3
    }
  },
  {
    id: 'technical',
    label: 'Technical',
    description: 'Orthographic with part edges and true colours, for documentation.',
    look: {
      background: 'solid',
      backgroundColor: '#ffffff',
      lens: 'ortho',
      edges: true,
      bloom: false,
      bevel: 0.25,
      vignette: 0,
      toneMapping: 'neutral'
    }
  },
  {
    id: 'cutout',
    label: 'Cut-out',
    description: 'Transparent PNG with its shadow, to drop onto slides and layouts.',
    look: { background: 'transparent', vignette: 0 },
    stillOnly: true
  },
  {
    id: 'monochrome',
    label: 'Black and white',
    description: 'Moody monochrome with grain, for posters and print.',
    look: { lighting: 'showroom', grade: 'mono', grain: 0.2, vignette: 0.4 }
  }
];

/** The full look `preset` gives: the defaults with its changes. */
export function presetLook(preset: RenderPreset): RenderLook {
  const look = {} as Record<(typeof LOOK_KEYS)[number], unknown>;
  for (const key of LOOK_KEYS) look[key] = preset.look[key] ?? DEFAULT_RENDER_STUDIO_SETTINGS[key];
  return look as RenderLook;
}

/** `settings` with `preset`'s look; everything outside the look is kept. */
export function withPreset(settings: RenderStudioSettings, preset: RenderPreset): RenderStudioSettings {
  return { ...settings, ...presetLook(preset) };
}

/** The preset whose look `settings` has exactly, or null once any look setting is changed by hand. */
export function matchingPreset(settings: RenderStudioSettings): RenderPresetId | null {
  const match = RENDER_PRESETS.find((preset) => {
    const look = presetLook(preset);
    return LOOK_KEYS.every((key) => settings[key] === look[key]);
  });
  return match?.id ?? null;
}

// ── Turntable ─────────────────────────────────────────────────────────

export const TURNTABLE_FPS = 30;
/** Range of the turntable's Length control, seconds. */
export const TURNTABLE_SECONDS = { min: 3, max: 20 } as const;
const VIDEO_LONG_EDGE: Readonly<Record<VideoSizeId, number>> = { hd: 1920, uhd: 3840 };
/**
 * Bits per pixel per frame, for encoders that can't code at constant quality
 * (see Mp4VideoWriter): they tend to stay well under the target on clean
 * rendered frames, so it is set high enough to keep edges and gradients clean.
 */
const VIDEO_BITS_PER_PIXEL = 0.2;

export function turntableFrames(seconds: number): number {
  return Math.max(2, Math.round(seconds * TURNTABLE_FPS));
}

/** Camera orbit for frame `index` of `frames`: one full turn from `startOrbit`, ending a frame short of it so the video loops. */
export function turntableOrbit(startOrbit: number, index: number, frames: number): number {
  return wrapDegrees(startOrbit + (360 * index) / frames);
}

/** Share of a morph video that shows the input model before it starts to dissolve. */
export const MORPH_START = 0.15;
/** Share of the video by which the input model is gone; the optimized model turns on its own after. */
export const MORPH_END = 0.7;

/** How far the input model has dissolved at frame `index` of `frames`, 0..1, easing in and out. */
export function morphProgress(index: number, frames: number): number {
  const time = frames > 1 ? index / (frames - 1) : 1;
  const t = Math.min(1, Math.max(0, (time - MORPH_START) / (MORPH_END - MORPH_START)));
  return t * t * t * (t * (t * 6 - 15) + 10);
}

/** Whether a video's camera turns around the model: always for a turntable, by choice for a morph. */
export function videoTurns(settings: Pick<RenderStudioSettings, 'animation' | 'morphTurntable'>): boolean {
  return settings.animation !== 'morph' || settings.morphTurntable;
}

/** Frame size of a turntable: the resolution's shape at the video size, in even pixels as video needs. */
export function videoOutputSize(resolution: RenderResolutionId, viewportAspect: number, videoSize: VideoSizeId): PixelSize {
  const { width, height } = scaleToLongEdge(renderOutputSize(resolution, viewportAspect), VIDEO_LONG_EDGE[videoSize]);
  return { width: Math.max(2, width - (width % 2)), height: Math.max(2, height - (height % 2)) };
}

/** Bitrate a video of `size` is encoded at where the encoder can't code at constant quality. */
export function videoBitrate({ width, height }: PixelSize): number {
  return Math.round(width * height * TURNTABLE_FPS * VIDEO_BITS_PER_PIXEL);
}

// ── Lighting ──────────────────────────────────────────────────────────

/**
 * A light placed relative to the shot: `azimuth` is degrees around the
 * world's up axis measured from the camera (0 = from behind the camera,
 * positive = from the right of the frame, 180 = from behind the model);
 * `elevation` is degrees above the horizon.
 */
export interface StudioLight {
  azimuth: number;
  elevation: number;
  intensity: number;
  color: string;
}

/** An emissive panel in the reflection environment, placed like a StudioLight. */
export interface StudioPanel {
  azimuth: number;
  elevation: number;
  width: number;
  height: number;
  intensity: number;
  color: string;
}

export interface StudioLightingPreset {
  label: string;
  /** Casts the shadows; softened by `shadowSoftness`. */
  key: StudioLight;
  others: readonly StudioLight[];
  /** Reflection environment: a two-tone room plus softbox panels. */
  room: { top: string; bottom: string; topIntensity: number; bottomIntensity: number };
  panels: readonly StudioPanel[];
  environmentIntensity: number;
  /** Radial studio backdrop, centre → edge. */
  gradient: { inner: string; outer: string };
  /** Colour of the curved studio backdrop. */
  cove: string;
  shadowOpacity: number;
}

export const STUDIO_LIGHTING: Readonly<Record<StudioLightingId, StudioLightingPreset>> = {
  softbox: {
    label: 'Studio softbox',
    key: { azimuth: -40, elevation: 52, intensity: 3.8, color: '#ffffff' },
    others: [
      { azimuth: 65, elevation: 18, intensity: 0.45, color: '#f2f5ff' },
      { azimuth: 165, elevation: 38, intensity: 1.8, color: '#ffffff' },
      { azimuth: 20, elevation: 12, intensity: 0.5, color: '#ffffff' }
    ],
    room: { top: '#ffffff', bottom: '#ffffff', topIntensity: 0.5, bottomIntensity: 0.15 },
    panels: [
      { azimuth: -35, elevation: 62, width: 9, height: 6, intensity: 3.5, color: '#ffffff' },
      { azimuth: -100, elevation: 12, width: 1.6, height: 9, intensity: 2.8, color: '#ffffff' },
      { azimuth: 100, elevation: 12, width: 1.6, height: 9, intensity: 2.2, color: '#ffffff' },
      { azimuth: 180, elevation: 28, width: 8, height: 2.2, intensity: 2.0, color: '#ffffff' },
      ...fillPanels(2.5, '#ffffff')
    ],
    environmentIntensity: 1,
    gradient: { inner: '#f3f4f6', outer: '#c3c7ce' },
    cove: '#e4e6ea',
    shadowOpacity: 0.5
  },
  showroom: {
    label: 'Dark showroom',
    key: { azimuth: -55, elevation: 42, intensity: 2.2, color: '#ffffff' },
    others: [
      { azimuth: 140, elevation: 22, intensity: 3.2, color: '#e8f0ff' },
      { azimuth: -145, elevation: 22, intensity: 2.4, color: '#ffffff' },
      { azimuth: 25, elevation: 12, intensity: 0.6, color: '#ffffff' }
    ],
    room: { top: '#ffffff', bottom: '#ffffff', topIntensity: 0.5, bottomIntensity: 0.15 },
    panels: [
      { azimuth: -50, elevation: 55, width: 6, height: 4, intensity: 4, color: '#ffffff' },
      { azimuth: -120, elevation: 10, width: 1, height: 10, intensity: 5, color: '#ffffff' },
      { azimuth: 120, elevation: 10, width: 1, height: 10, intensity: 5, color: '#dfe9ff' },
      ...fillPanels(3, '#ffffff')
    ],
    environmentIntensity: 1,
    gradient: { inner: '#2c3038', outer: '#0a0b0d' },
    cove: '#24272d',
    shadowOpacity: 0.7
  },
  catalogue: {
    label: 'High-key catalogue',
    key: { azimuth: -30, elevation: 62, intensity: 1.9, color: '#ffffff' },
    others: [
      { azimuth: 50, elevation: 22, intensity: 1.0, color: '#ffffff' },
      { azimuth: 170, elevation: 35, intensity: 0.8, color: '#ffffff' }
    ],
    room: { top: '#ffffff', bottom: '#ffffff', topIntensity: 1.1, bottomIntensity: 0.55 },
    panels: [
      { azimuth: -30, elevation: 65, width: 10, height: 7, intensity: 3, color: '#ffffff' },
      { azimuth: 90, elevation: 15, width: 3, height: 9, intensity: 1.6, color: '#ffffff' }
    ],
    environmentIntensity: 1.1,
    gradient: { inner: '#ffffff', outer: '#eceef1' },
    cove: '#f4f5f7',
    shadowOpacity: 0.32
  },
  dusk: {
    label: 'Warm dusk',
    key: { azimuth: -70, elevation: 22, intensity: 3.4, color: '#ffb46e' },
    others: [
      { azimuth: 75, elevation: 20, intensity: 0.7, color: '#86a8ff' },
      { azimuth: 160, elevation: 25, intensity: 2.2, color: '#ffc894' },
      { azimuth: 20, elevation: 12, intensity: 0.5, color: '#ffe2c8' }
    ],
    room: { top: '#7e9cff', bottom: '#ff9a5a', topIntensity: 0.5, bottomIntensity: 0.25 },
    panels: [
      { azimuth: -75, elevation: 18, width: 7, height: 3, intensity: 5, color: '#ffb46e' },
      { azimuth: 90, elevation: 30, width: 6, height: 5, intensity: 1.4, color: '#9db6ff' },
      ...fillPanels(3.5, '#ffe2c8')
    ],
    environmentIntensity: 1,
    gradient: { inner: '#3b2c25', outer: '#0e0a09' },
    cove: '#3a2f2a',
    shadowOpacity: 0.6
  }
};

/**
 * Broad, dim panels behind the camera, at both front sides and below, at
 * `intensity` for the one behind the camera. Faces turned to the camera
 * mirror what is behind it, so without these a dark, metallic part shows
 * them black, whatever the key and rim panels do.
 */
function fillPanels(intensity: number, color: string): StudioPanel[] {
  return [
    { azimuth: 25, elevation: 18, width: 16, height: 8, intensity, color },
    { azimuth: -70, elevation: 10, width: 6, height: 8, intensity: intensity / 2, color },
    { azimuth: 70, elevation: 10, width: 6, height: 8, intensity: intensity / 2, color },
    { azimuth: 0, elevation: -15, width: 14, height: 4, intensity: intensity / 3, color }
  ];
}

export const LIGHTING_OPTIONS: readonly LabelledOption<StudioLightingId>[] = (
  Object.keys(STUDIO_LIGHTING) as StudioLightingId[]
).map((value) => ({ value, label: STUDIO_LIGHTING[value].label }));

// ── Materials ─────────────────────────────────────────────────────────

export interface StudioMaterialPreset {
  label: string;
  color: string;
  metalness: number;
  roughness: number;
  clearcoat?: number;
  clearcoatRoughness?: number;
  sheen?: number;
  /** A pattern drawn on the surface in the shader: brushing streaks or a carbon-fibre weave. */
  surface?: StudioSurfaceId;
}

/** Procedural surfaces; the CAD meshes carry no UVs, so both are laid out from world position. */
export type StudioSurfaceId = 'brushed' | 'carbon';

/** `null` keeps every part's own colour and finish. */
export const STUDIO_MATERIALS: Readonly<Record<StudioMaterialId, StudioMaterialPreset | null>> = {
  'as-shown': null,
  aluminium: { label: 'Machined aluminium', color: '#d5d8dc', metalness: 1, roughness: 0.3 },
  'blasted-aluminium': { label: 'Bead-blasted aluminium', color: '#c9ccd0', metalness: 1, roughness: 0.55 },
  'brushed-aluminium': { label: 'Brushed aluminium', color: '#d2d5d9', metalness: 1, roughness: 0.3, surface: 'brushed' },
  titanium: { label: 'Polished titanium', color: '#c6bfb4', metalness: 1, roughness: 0.2 },
  steel: { label: 'Satin steel', color: '#b5b9be', metalness: 1, roughness: 0.42 },
  'brushed-steel': { label: 'Brushed stainless steel', color: '#babdc1', metalness: 1, roughness: 0.28, surface: 'brushed' },
  chrome: { label: 'Chrome', color: '#eef0f2', metalness: 1, roughness: 0.12 },
  'cast-iron': { label: 'Cast iron', color: '#7c7d7f', metalness: 1, roughness: 0.72 },
  brass: { label: 'Brass', color: '#e1c27c', metalness: 1, roughness: 0.25 },
  copper: { label: 'Copper', color: '#eba584', metalness: 1, roughness: 0.25 },
  'anodized-black': anodised('Anodised black', '#2a2b30'),
  'anodized-blue': anodised('Anodised blue', '#2c5d9f'),
  'anodized-red': anodised('Anodised red', '#a3292f'),
  'anodized-gold': anodised('Anodised gold', '#c9a14a'),
  'powder-white': {
    label: 'Powder coat, white',
    color: '#eceeef',
    metalness: 0,
    roughness: 0.5,
    clearcoat: 0.3,
    clearcoatRoughness: 0.35
  },
  'plastic-black': { label: 'Moulded plastic, black', color: '#1d1e21', metalness: 0, roughness: 0.4 },
  rubber: { label: 'Rubber', color: '#1b1b1c', metalness: 0, roughness: 0.9, sheen: 0.2 },
  nylon: { label: 'SLS nylon, white', color: '#ebeae4', metalness: 0, roughness: 0.85, sheen: 0.4 },
  'resin-grey': { label: 'SLA resin, grey', color: '#8e9296', metalness: 0, roughness: 0.35 },
  'carbon-fibre': {
    label: 'Carbon fibre, 2×2 twill',
    color: '#34373d',
    metalness: 0.35,
    roughness: 0.3,
    clearcoat: 1,
    clearcoatRoughness: 0.03,
    surface: 'carbon'
  },
  clay: { label: 'Clay', color: '#d9d3c9', metalness: 0, roughness: 0.75 }
};

/** Anodised aluminium: the dye over the metal, under a thin sealed layer. */
function anodised(label: string, color: string): StudioMaterialPreset {
  return { label, color, metalness: 0.6, roughness: 0.38, clearcoat: 0.5, clearcoatRoughness: 0.15 };
}

export const MATERIAL_OPTIONS: readonly LabelledOption<StudioMaterialId>[] = (
  Object.keys(STUDIO_MATERIALS) as StudioMaterialId[]
).map((value) => ({ value, label: STUDIO_MATERIALS[value]?.label ?? 'As shown in the editor' }));

export const PART_MATERIAL_OPTIONS: readonly LabelledOption<PartMaterialChoice>[] = [
  { value: 'model', label: 'Same as model' },
  ...MATERIAL_OPTIONS
];

/** The preset part `partKey` renders with: its own choice, else the model-wide one. `null` keeps the editor's look. */
export function partMaterial(
  settings: Pick<RenderStudioSettings, 'material' | 'partMaterials'>,
  partKey: string | null
): StudioMaterialPreset | null {
  const own = partKey === null ? undefined : settings.partMaterials[partKey];
  return STUDIO_MATERIALS[own ?? settings.material];
}

/** `partMaterials` with part `partKey` set to `choice`; 'model' removes the override. */
export function withPartMaterial(
  partMaterials: Readonly<Record<string, StudioMaterialId>>,
  partKey: string,
  choice: PartMaterialChoice
): Readonly<Record<string, StudioMaterialId>> {
  const next = { ...partMaterials };
  if (choice === 'model') delete next[partKey];
  else next[partKey] = choice;
  return next;
}

// ── Pure helpers ──────────────────────────────────────────────────────

export interface PixelSize {
  width: number;
  height: number;
}

/**
 * Output size in pixels. `viewportAspect` (width / height) is only used by
 * 'viewport'; the result keeps a 3840 px long edge and even dimensions.
 */
export function renderOutputSize(resolution: RenderResolutionId, viewportAspect: number): PixelSize {
  switch (resolution) {
    case 'uhd':
      return { width: 3840, height: 2160 };
    case 'uhd-portrait':
      return { width: 2160, height: 3840 };
    case 'square':
      return { width: RENDER_LONG_EDGE, height: RENDER_LONG_EDGE };
    case 'viewport': {
      const aspect = viewportAspect > 0 && Number.isFinite(viewportAspect) ? viewportAspect : 16 / 9;
      const even = (n: number): number => Math.max(2, Math.round(n / 2) * 2);
      return aspect >= 1
        ? { width: RENDER_LONG_EDGE, height: even(RENDER_LONG_EDGE / aspect) }
        : { width: even(RENDER_LONG_EDGE * aspect), height: RENDER_LONG_EDGE };
    }
  }
}

/** The same shape at a smaller long edge, for the live preview. */
export function scaleToLongEdge(size: PixelSize, longEdge: number): PixelSize {
  const scale = longEdge / Math.max(size.width, size.height);
  if (scale >= 1) return { ...size };
  return {
    width: Math.max(1, Math.round(size.width * scale)),
    height: Math.max(1, Math.round(size.height * scale))
  };
}

/**
 * Vertical field of view in degrees for a full-frame (36 × 24 mm) lens. The
 * 24 mm side always maps to the image's short side, so a portrait output
 * frames like the camera turned on its side.
 */
export function verticalFovDegrees(focalLengthMm: number, aspect: number): number {
  const halfShort = Math.atan(12 / focalLengthMm);
  const halfVertical = aspect >= 1 ? halfShort : Math.atan(Math.tan(halfShort) / aspect);
  return (2 * halfVertical * 180) / Math.PI;
}

/** Grows a half-extent pair to `aspect` without cropping either side. */
export function containExtents(halfWidth: number, halfHeight: number, aspect: number): { halfWidth: number; halfHeight: number } {
  if (halfWidth / halfHeight < aspect) return { halfWidth: halfHeight * aspect, halfHeight };
  return { halfWidth, halfHeight: halfWidth / aspect };
}

/** Radical inverse in `base`: a low-discrepancy sequence in [0, 1). */
export function halton(index: number, base: number): number {
  let result = 0;
  let f = 1 / base;
  let i = index;
  while (i > 0) {
    result += f * (i % base);
    i = Math.floor(i / base);
    f /= base;
  }
  return result;
}

const PLASTIC = 1.324717957244746;

/**
 * Point `index` of Roberts' R2 sequence in the unit square. Unlike Halton in
 * large bases, it is evenly spread at every count, so a few dozen samples
 * never line up. `stream` shifts it, so several jittered things don't move in
 * step.
 */
export function r2(index: number, stream = 0): [number, number] {
  const frac = (x: number): number => x - Math.floor(x);
  return [frac(0.5 + stream * 0.6180339887 + index / PLASTIC), frac(0.5 + stream * 0.4142135624 + index / (PLASTIC * PLASTIC))];
}

/** Maps a unit-square sample onto the unit disk, keeping the samples evenly spread (Shirley–Chiu). */
export function concentricDisk(u: number, v: number): [number, number] {
  const a = 2 * u - 1;
  const b = 2 * v - 1;
  if (a === 0 && b === 0) return [0, 0];
  let r: number;
  let phi: number;
  if (Math.abs(a) > Math.abs(b)) {
    r = a;
    phi = (Math.PI / 4) * (b / a);
  } else {
    r = b;
    phi = Math.PI / 2 - (Math.PI / 4) * (a / b);
  }
  return [r * Math.cos(phi), r * Math.sin(phi)];
}

/** Unit vector for a shot-relative direction, in the shot frame (x right, y up, z towards the camera). */
export function shotDirection(azimuthDeg: number, elevationDeg: number): [number, number, number] {
  const az = (azimuthDeg * Math.PI) / 180;
  const el = (elevationDeg * Math.PI) / 180;
  return [Math.sin(az) * Math.cos(el), Math.sin(el), Math.cos(az) * Math.cos(el)];
}

/** File name for a render: the project name made safe, the pixel size and a timestamp. */
export function renderFileName(
  projectName: string | null | undefined,
  size: PixelSize,
  now: Date = new Date(),
  extension: 'png' | 'mp4' = 'png'
): string {
  const safe =
    (projectName || 'project')
      .replace(/[^\w\- ]+/g, '')
      .trim()
      .replace(/\s+/g, '_') || 'project';
  const pad = (n: number): string => String(n).padStart(2, '0');
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
  return `${safe}_${extension === 'mp4' ? 'turntable' : 'render'}_${size.width}x${size.height}_${stamp}.${extension}`;
}

/** Wraps an angle to [−180, 180) degrees. */
export function wrapDegrees(degrees: number): number {
  return ((((degrees + 180) % 360) + 360) % 360) - 180;
}

/**
 * The tilt that can actually be applied to a camera at `baseElevation`
 * (degrees above the horizon it looks down from), keeping the result within
 * ±MAX_ELEVATION. A camera already beyond that (a top view) only tilts back.
 */
export function clampTilt(baseElevation: number, tilt: number): number {
  const low = Math.min(-MAX_ELEVATION, baseElevation);
  const high = Math.max(MAX_ELEVATION, baseElevation);
  return Math.min(high, Math.max(low, baseElevation + tilt)) - baseElevation;
}
